import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaUpload } from 'react-icons/fa';

const emptyForm = { name: '', slug: '', description: '', image_url: '', display_order: 0 };

export default function AdminCategories() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = () => axios.get('/api/categories/all', { headers }).then(r => setCategories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const autoSlug = (name) => name.toLowerCase()
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setModal(true); };
  const openEdit = (cat) => { setForm({ ...cat, image_url: cat.image_url || '' }); setEditId(cat.id); setError(''); setModal(true); };
  const close = () => { setModal(false); setError(''); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editId) await axios.put(`/api/categories/${editId}`, form, { headers });
      else await axios.post('/api/categories', form, { headers });
      load(); close();
    } catch (err) { setError(err.response?.data?.message || 'Hata oluştu'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz?')) return;
    await axios.delete(`/api/categories/${id}`, { headers });
    load();
  };

  const handleToggle = async (id) => {
    await axios.patch(`/api/categories/${id}/toggle`, {}, { headers });
    load();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('image', file);
    try {
      const { data } = await axios.post('/api/upload', fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch { alert('Yükleme başarısız'); }
    setUploading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">Kategoriler</h1>
          <p className="text-gray-500 text-sm">{categories.length} kategori</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm py-2.5">
          <FaPlus /> Kategori Ekle
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-5 py-3 text-left font-medium text-gray-500">Kategori Adı</th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">URL Kodu</th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Sıra</th>
              <th className="px-5 py-3 text-left font-medium text-gray-500">Durum</th>
              <th className="px-5 py-3 text-right font-medium text-gray-500">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {categories.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-5 py-3.5 font-medium text-gray-700">{c.name}</td>
                <td className="px-5 py-3.5 text-gray-400 text-xs hidden sm:table-cell font-mono">{c.slug}</td>
                <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{c.display_order}</td>
                <td className="px-5 py-3.5">
                  <button onClick={() => handleToggle(c.id)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${c.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    {c.is_active ? 'Aktif' : 'Pasif'}
                  </button>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(c)} className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors">
                      <FaEdit size={14} />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <FaTrash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {categories.length === 0 && (
          <div className="text-center py-12 text-gray-400">Henüz kategori eklenmemiş</div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-700">{editId ? 'Kategori Düzenle' : 'Yeni Kategori'}</h3>
              <button onClick={close} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Kategori Adı *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: !editId ? autoSlug(e.target.value) : f.slug }))}
                  className="input-field" placeholder="Kategori adı" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">URL Kodu *
                  <span className="text-gray-400 font-normal text-xs ml-1">(addan otomatik oluşur)</span>
                </label>
                <input value={form.slug} readOnly
                  className="input-field font-mono text-sm bg-gray-50 cursor-default text-gray-500" placeholder="kategori-url-kodu" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Açıklama</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} className="input-field resize-none" placeholder="Kategori açıklaması" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Görsel</label>
                <div className="flex gap-2">
                  <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    className="input-field flex-1" placeholder="URL veya dosya yükleyin" />
                  <label className="btn-secondary text-sm py-3 px-4 cursor-pointer flex-shrink-0">
                    {uploading ? '...' : <><FaUpload size={13} /> Yükle</>}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
                {form.image_url && (
                  <img src={form.image_url} alt="" className="mt-2 h-20 rounded-lg object-cover" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Sıra</label>
                  <input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: +e.target.value }))}
                    className="input-field" />
                </div>
                {editId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Durum</label>
                    <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))} className="input-field">
                      <option value={1}>Aktif</option>
                      <option value={0}>Pasif</option>
                    </select>
                  </div>
                )}
              </div>
              {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-2 text-sm">{error}</div>}
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={close} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors text-sm font-medium">İptal</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm disabled:opacity-60">
                {saving ? 'Kaydediliyor...' : <><FaSave size={13} /> Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
